// ShareDeal.js - "Share link / Email me this / Save as PDF" for a deal.
//
// Added Oct 2026. The server builds a frozen copy (snapshot) of this trip's
// current fare under a random link that works for 30 days. The public page
// shows the route, dates, price and flights, never a name, email or booking
// link. See backend app/routes/share.py.
import React, { useState } from 'react';
import { theme } from './theme';
import { createShareLink, emailShare } from './api/atlast';
import { friendlyDetail } from './utils/dealExtras';

const linkStyle = {
  background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer',
  color: theme.structural, fontWeight: 700, fontSize: '12.5px', textDecoration: 'underline',
  fontFamily: 'inherit',
};

function messageFrom(error) {
  const d = error?.response?.data?.detail;
  if (d && typeof d === 'object' && d.message) return friendlyDetail(d.message);
  if (typeof d === 'string') return friendlyDetail(d);
  return 'We couldn\'t do that just now. Please try again.';
}

export default function ShareDeal({ pinId, variant }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const shareLink = async () => {
    if (busy) return;
    setBusy(true); setNote('');
    try {
      const { url } = await createShareLink(pinId);
      if (navigator.share) {
        await navigator.share({ title: 'A saved fare on FlyAtlast', url }).catch(() => {});
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setNote('Link copied. It works for 30 days.');
      } else {
        window.prompt('Copy this link:', url);
      }
    } catch (e) {
      setNote(messageFrom(e));
    } finally {
      setBusy(false);
    }
  };

  const pdf = async () => {
    if (busy) return;
    setBusy(true); setNote('');
    const w = window.open('', '_blank');
    try {
      const { url } = await createShareLink(pinId);
      const target = `${url}&pdf=1`;
      if (w) w.location = target; else window.location.href = target;
    } catch (e) {
      if (w) w.close();
      setNote(messageFrom(e));
    } finally {
      setBusy(false);
    }
  };

  const email = async () => {
    if (busy) return;
    setBusy(true); setNote('');
    try {
      await emailShare(pinId);
      setNote('Sent to your account email. Check your inbox (and spam).');
    } catch (e) {
      setNote(messageFrom(e));
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'buttons') {
    // Prominent version (Oct 10, per Ali's approved mockup): three rounded
    // outline buttons with icons, used in the page header.
    const icon = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
    const btn = {
      display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fff', color: theme.structural,
      border: `2px solid ${theme.structural}`, borderRadius: '12px', padding: '10px 14px', fontWeight: 800,
      fontSize: '13.5px', cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.7 : 1,
    };
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="button" style={btn} onClick={shareLink} disabled={busy}>
            <svg {...icon}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 10.5l6.8-4M8.6 13.5l6.8 4" /></svg>
            {navigator.share ? 'Share link' : 'Copy link'}
          </button>
          <button type="button" style={btn} onClick={email} disabled={busy}>
            <svg {...icon}><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 8l9 6 9-6" /></svg>
            Email me this
          </button>
          <button type="button" style={btn} onClick={pdf} disabled={busy}>
            <svg {...icon}><path d="M12 4v11M7 11l5 5 5-5M5 20h14" /></svg>
            Save as PDF
          </button>
        </div>
        {note && <p role="status" style={{ fontSize: '12px', color: theme.textMuted, margin: '8px 0 0' }}>{note}</p>}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '14px', textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '2px 16px' }}>
        <button type="button" style={linkStyle} onClick={shareLink} disabled={busy}>
          {navigator.share ? 'Share link' : 'Copy link'}
        </button>
        <button type="button" style={linkStyle} onClick={email} disabled={busy}>Email me this</button>
        <button type="button" style={linkStyle} onClick={pdf} disabled={busy}>Save as PDF</button>
      </div>
      {note && <p role="status" style={{ fontSize: '12.5px', color: theme.textMuted, margin: '4px 0 0' }}>{note}</p>}
    </div>
  );
}
